/**
 * Tooltip Positioning Utilities
 *
 * Provides smart positioning for tooltips on the property map navigator.
 * Implements an 8-position variant algorithm that detects overflow on all 4 edges
 * and selects the optimal position to keep tooltips fully visible.
 *
 * Optimized for mobile devices of all sizes (<375px, 375-428px, 768px+).
 */

/**
 * Calculate if a tooltip would overflow container boundaries
 *
 * @param {Object} position - Tooltip position {x, y}
 * @param {Object} dimensions - Tooltip dimensions {width, height}
 * @param {DOMRect} containerRect - Container boundaries from getBoundingClientRect()
 * @param {number} offset - Offset from cursor (default 10)
 * @returns {Object} Overflow info:
 *   - top: boolean - Overflows top edge
 *   - right: boolean - Overflows right edge
 *   - bottom: boolean - Overflows bottom edge
 *   - left: boolean - Overflows left edge
 *   - overflowX: number - Horizontal overflow amount (negative = left, positive = right)
 *   - overflowY: number - Vertical overflow amount (negative = top, positive = bottom)
 */
export function calculateOverflow(position, dimensions, containerRect, offset = 10) {
  // Calculate tooltip bounding rectangle
  const tooltipRect = {
    left: position.x,
    top: position.y,
    right: position.x + dimensions.width,
    bottom: position.y + dimensions.height
  };

  // Check each edge for overflow
  const overflow = {
    top: tooltipRect.top < 0,
    right: tooltipRect.right > containerRect.width,
    bottom: tooltipRect.bottom > containerRect.height,
    left: tooltipRect.left < 0,
    // Calculate overflow amounts for fallback positioning
    overflowX: tooltipRect.right > containerRect.width
      ? tooltipRect.right - containerRect.width
      : tooltipRect.left < 0
        ? tooltipRect.left
        : 0,
    overflowY: tooltipRect.bottom > containerRect.height
      ? tooltipRect.bottom - containerRect.height
      : tooltipRect.top < 0
        ? tooltipRect.top
        : 0
  };

  return overflow;
}

/**
 * Generate all 8 possible position variants for tooltip around cursor/touch point
 *
 * Position variants (priority order):
 * 1. right-below  - Default position (right and slightly above cursor)
 * 2. left-below   - Mirror of default (left and slightly above cursor)
 * 3. right-above  - Above cursor, right side
 * 4. left-above   - Above cursor, left side
 * 5. right-center - Right side, vertically centered on cursor
 * 6. left-center  - Left side, vertically centered on cursor
 * 7. center-above - Horizontally centered, above cursor
 * 8. center-below - Horizontally centered, below cursor
 *
 * @param {Object} cursor - Cursor/touch position {x, y} relative to container
 * @param {Object} dimensions - Tooltip dimensions {width, height}
 * @param {number} offset - Offset from cursor (default 10)
 * @returns {Array<Object>} Array of position variants with:
 *   - name: string - Position variant name
 *   - x: number - Left position
 *   - y: number - Top position
 *   - priority: number - Selection priority (1 = highest)
 */
export function generatePositionVariants(cursor, dimensions, offset = 10) {
  const { width, height } = dimensions;

  return [
    // Priority 1: Right-Below (default)
    // Tooltip to the right and slightly above cursor
    {
      name: 'right-below',
      x: cursor.x + offset,
      y: cursor.y - offset,
      priority: 1
    },

    // Priority 2: Left-Below
    // Tooltip to the left and slightly above cursor
    {
      name: 'left-below',
      x: cursor.x - width - offset,
      y: cursor.y - offset,
      priority: 2
    },

    // Priority 3: Right-Above
    // Tooltip above cursor, aligned to right
    {
      name: 'right-above',
      x: cursor.x + offset,
      y: cursor.y - height + offset,
      priority: 3
    },

    // Priority 4: Left-Above
    // Tooltip above cursor, aligned to left
    {
      name: 'left-above',
      x: cursor.x - width - offset,
      y: cursor.y - height + offset,
      priority: 4
    },

    // Priority 5: Right-Center
    // Tooltip to right, vertically centered on cursor
    {
      name: 'right-center',
      x: cursor.x + offset,
      y: cursor.y - (height / 2),
      priority: 5
    },

    // Priority 6: Left-Center
    // Tooltip to left, vertically centered on cursor
    {
      name: 'left-center',
      x: cursor.x - width - offset,
      y: cursor.y - (height / 2),
      priority: 6
    },

    // Priority 7: Center-Above
    // Tooltip horizontally centered, above cursor
    {
      name: 'center-above',
      x: cursor.x - (width / 2),
      y: cursor.y - height - offset,
      priority: 7
    },

    // Priority 8: Center-Below
    // Tooltip horizontally centered, below cursor
    {
      name: 'center-below',
      x: cursor.x - (width / 2),
      y: cursor.y + offset,
      priority: 8
    }
  ];
}

/**
 * Select the best tooltip position that avoids overflow
 *
 * Algorithm:
 * 1. Generate all 8 position variants
 * 2. Iterate through variants by priority (1-8)
 * 3. Check overflow for each variant on all 4 edges
 * 4. Return first variant with no overflow
 * 5. If no perfect position exists:
 *    - Calculate overflow score for each variant
 *    - Select variant with minimal overflow
 *    - Clamp position to container boundaries
 *
 * @param {Object} cursor - Cursor position {x, y} relative to container
 * @param {Object} dimensions - Tooltip dimensions {width, height}
 * @param {DOMRect} containerRect - Container boundaries
 * @param {number} offset - Offset from cursor (default 10)
 * @returns {Object} Best position:
 *   - name: string - Position variant name
 *   - x: number - Left position (clamped to container)
 *   - y: number - Top position (clamped to container)
 *   - priority: number - Original priority
 */
export function selectBestPosition(cursor, dimensions, containerRect, offset = 10) {
  const variants = generatePositionVariants(cursor, dimensions, offset);

  // Phase 1: Find first position with no overflow
  for (const variant of variants) {
    const overflow = calculateOverflow(
      { x: variant.x, y: variant.y },
      dimensions,
      containerRect,
      offset
    );

    // Perfect position found - no overflow on any edge
    if (!overflow.top && !overflow.right && !overflow.bottom && !overflow.left) {
      return variant;
    }
  }

  // Phase 2: No perfect position - find least bad option
  let bestVariant = variants[0];
  let minOverflowScore = Infinity;

  for (const variant of variants) {
    const overflow = calculateOverflow(
      { x: variant.x, y: variant.y },
      dimensions,
      containerRect,
      offset
    );

    // Calculate total overflow score
    // We use absolute values to penalize any overflow equally
    const overflowScore = Math.abs(overflow.overflowX) + Math.abs(overflow.overflowY);

    if (overflowScore < minOverflowScore) {
      minOverflowScore = overflowScore;
      bestVariant = variant;
    }
  }

  // Phase 3: Clamp position to container boundaries
  // This ensures tooltip is always at least partially visible
  const clampedPosition = {
    ...bestVariant,
    x: Math.max(0, Math.min(bestVariant.x, containerRect.width - dimensions.width)),
    y: Math.max(0, Math.min(bestVariant.y, containerRect.height - dimensions.height))
  };

  return clampedPosition;
}
